import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  IconButton,
  useColorModeValue,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { LeadAction } from '../../types';

export interface LeadActionsListProps {
  actions: LeadAction[];
  loading?: boolean;
  onComplete?: (actionId: number) => void;
  onActionClick?: (action: LeadAction) => void;
  emptyText?: string;
  labels?: {
    overdue?: string;
    upcoming?: string;
    completeAction?: string;
  };
  completingActionId?: number | null;
}

export const LeadActionsList: FC<LeadActionsListProps> = ({
  actions,
  loading = false,
  onComplete,
  onActionClick,
  emptyText,
  labels,
  completingActionId,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const mergedLabels = {
    overdue: 'Overdue',
    upcoming: 'Upcoming',
    completeAction: 'Complete action',
    ...labels,
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="lg" />
      </Center>
    );
  }

  const sortedActions = [...actions].sort((a, b) => {
    if (a.completed_at && !b.completed_at) return 1;
    if (!a.completed_at && b.completed_at) return -1;
    return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
  });

  return (
    <VStack align="stretch" spacing={2}>
      {sortedActions.map((action) => {
        const isCompleted = !!action.completed_at;
        const isOverdue = !isCompleted && new Date(action.scheduled_at) < new Date();
        const isUpcoming = !isCompleted && new Date(action.scheduled_at) > new Date();

        return (
          <Box
            key={action.id}
            p={3}
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="md"
            cursor={onActionClick ? 'pointer' : 'default'}
            onClick={() => onActionClick?.(action)}
            _hover={onActionClick ? { bg: hoverBg } : {}}
            opacity={isCompleted ? 0.6 : 1}
          >
            <HStack justify="space-between">
              <VStack align="start" spacing={1} flex={1}>
                <HStack>
                  <Text fontSize="sm" fontWeight="medium" textDecoration={isCompleted ? 'line-through' : 'none'}>
                    {action.title}
                  </Text>
                  {isOverdue && (
                    <Badge colorScheme="red" size="sm">
                      {mergedLabels.overdue}
                    </Badge>
                  )}
                  {isUpcoming && (
                    <Badge colorScheme="blue" size="sm">
                      {mergedLabels.upcoming}
                    </Badge>
                  )}
                </HStack>
                {action.description && (
                  <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')}>
                    {action.description}
                  </Text>
                )}
                <Text fontSize="xs" color={useColorModeValue('gray.500', 'gray.500')}>
                  {new Date(action.scheduled_at).toLocaleString()}
                </Text>
              </VStack>
              {!isCompleted && onComplete && (
                <IconButton
                  aria-label={mergedLabels.completeAction}
                  icon={<CheckIcon />}
                  size="sm"
                  colorScheme="green"
                  isLoading={completingActionId === action.id}
                  isDisabled={completingActionId === action.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete(action.id);
                  }}
                />
              )}
            </HStack>
          </Box>
        );
      })}
      {actions.length === 0 && (
        <Center h="100px">
          <Text color={useColorModeValue('gray.500', 'gray.400')}>
            {emptyText || 'No actions scheduled'}
          </Text>
        </Center>
      )}
    </VStack>
  );
};

